import {
  add,
  calendar,
  day,
  diff,
  endOf,
  format,
  fromNow,
  humanizeDuration,
  isAfter,
  isSame,
  isSameOrBefore,
  isBefore as libIsBefore,
  now,
  parse,
  parseFormat,
  startOf,
  subtract,
  toDate,
  tz,
  utc,
  year,
} from 'app/lib/date'
import * as helpers from 'app/sm/helpers'

export const STANDARD_DATETIME = 'hh:mma, ddd DD MMM YYYY'
export const SHORT_STANDARD_DATETIME = 'hh:mma DD MMM YYYY'
export const STANDARD_DATE = 'DD MMM YYYY'
export const AUSTRALIAN_HUMAN_READABLE_DATETIME = 'DD MMM YYYY, h:mm a'
export const TIME_AM_PM_FULL_DAY = 'hh:mmA dddd'
export const TIME_AM_PM_HALF_DAY_HALF_MONTH = 'hh:mm A, ddd DD MMM YYYY'
export const HALF_DAY_HALF_MONTH_TIME_AM_PM = 'DD MMM YYYY, hh:mm A'
export const HALF_DAY_HALF_MONTH_TIME_SMALL_AM_PM = 'D MMM YYYY, h:mma'
export const REGISTER_DATETIME = 'ddd DD MMM h:mma'

export const DATE_WITH_DASH = 'YYYY-MM-DD'
export const YEAR_DASH_MONTH = 'YYYY-MM'
export const DATE_DAY_SHORT_STRING_MONTH_YEAR = 'D MMM YYYY'
export const HALF_DATE_WITHOUT_YEAR_MILITARY_TIME = 'D MMM H:ss'
export const HALF_DATE_WITHOUT_YEAR = 'D MMM'
export const HALF_DAY_FULL_MONTH = 'D MMMM'
export const HALF_DAY = 'ddd'
export const TIME_AM_PM = 'h:mma'
export const DATE_NO_YEAR = 'DD MMM'
export const DATE_WITH_SLASH = 'DD/MM/YYYY'
export const TIME_AM_PM_WITHOUT_FULL_HOUR = 'h:mm A'
export const HUMAN_READABLE_DATE = 'ddd, DD MMM'
export const HOUR_DAY_MONTH_YEAR_FORMAT = 'HH:MM DD MMM YYYY'
export const INVITED_DATE_FORMAT = HOUR_DAY_MONTH_YEAR_FORMAT
export const TIME_DAY_HALF_MONTH = 'h:mm A, DD MMM'
export const FULL_TIME_24H = 'HH:mm'
export const FULL_TIME_24H_Z = 'HH:mm:ssZ'
export const TRIMMED_DAY_MONTH_YEAR_FORMAT = 'DDMMMYYYY'
export const TIME_AM_PM_DATE = 'hh:mm A DD MMM YYYY'
export const SHORT_DAY_FULL_DATE = 'ddd DD MMM YYYY'
export const DisplayDateFormat = 'ddd D MMM YYYY'
export const DisplayDateWithTimeFormat = 'ddd D MMM, h:mm A'
export const TIME_DAY_DATE = 'h:mm a, DD MMM YYYY'
export const DOT_DATE_FORMAT = 'D.MM.YY'
export const HOUR_MINUTE_DATE_MONTH_YEAR = 'HH:mm  DD MM YYYY'

export const shortYearDateTime = 'DD MMM YY HH:mm'
export const shortYearDateTimeWithAt = 'DD MMM YY [at] HH:mm'
export const FULL_HOUR_AM_PM = 'hh:mm A'

export const DEFAULT_UNIX_TIME = '0001-01-01T00:00:00Z'
export const DEFAULT_UNIX_TIME_Z = 'YYYY-MM-DDTHH:mm:ss[Z]'
export const DATE_TIME_Z = 'YYYY-MM-DDTHH:mm:ssZ'

export const INVALID_DATE_STRING = 'Invalid date'

export const QR_CODE_FORMAT_FILENAME = 'YYYYMMDDHm'

export const YEAR_ONLY = 'YYYY'

export const AEDT_UTC = +11
export const MONTHS_GUIDs = [
  { guid: 1, name: 'Jan' },
  { guid: 2, name: 'Feb' },
  { guid: 3, name: 'Mar' },
  { guid: 4, name: 'Apr' },
  { guid: 5, name: 'May' },
  { guid: 6, name: 'Jun' },
  { guid: 7, name: 'Jul' },
  { guid: 8, name: 'Aug' },
  { guid: 9, name: 'Sep' },
  { guid: 10, name: 'Oct' },
  { guid: 11, name: 'Nov' },
  { guid: 12, name: 'Dec' },
]

const replaceTimePeriods = {
  seconds: 'secs',
  second: 'sec',
  minute: 'min',
  minutes: 'mins',
  hour: 'hr',
  hours: 'hrs',
  month: 'mth',
  months: 'mths',
  year: 'yr',
  years: 'yrs',
  'a ': '1 ',
}

export const hourDayMonthOrYearDifference = (date) => {
  const relativeDurationFromNow = fromNow(date, true)
  const formattedDateAndTime = format(
    date,
    HALF_DATE_WITHOUT_YEAR_MILITARY_TIME,
  )
  const regexForTimePeriods = new RegExp(
    Object.keys(replaceTimePeriods).join('|'),
    'gi',
  )
  const formattedTimePeriodFromNow = relativeDurationFromNow.replace(
    regexForTimePeriods,
    (matched) => replaceTimePeriods[matched],
  )
  return `${formattedDateAndTime} (${formattedTimePeriodFromNow})`
}

export const nowOrProvidedTimestamp = (isNow, timestamp) => {
  let result = now()
  if (!isNow && timestamp) result = parse(timestamp)
  return result
}

export const standardFormattedTimeStamp = (timestamp) => {
  if (!!timestamp) return timestamp.local().format(STANDARD_DATETIME)
}

export const convertDateToDayDateAndTime = (date) => {
  const dt = parse(date)
  let dayOrDate = format(dt, HALF_DATE_WITHOUT_YEAR)
  const time = format(dt, TIME_AM_PM)
  const diffBetweenDates = diff(dt, now(), 'days', true)
  if (diffBetweenDates >= 0 && Math.round(diffBetweenDates) < 7) {
    dayOrDate = format(dt, HALF_DAY)
  }
  if (isSame(dt, now(), 'day')) {
    dayOrDate = 'Today'
  }
  return {
    dayOrDate,
    time,
  }
}

export const convertDateToDayAndDate = (date) => {
  const dt = parse(date)
  let dayStr = format(dt, 'dddd')
  const dateWithoutYear = format(dt, DATE_NO_YEAR)
  if (isSame(dt, now(), 'day')) {
    dayStr = 'Today'
  }
  return {
    day: dayStr,
    dateWithoutYear,
  }
}

export const calculateAge = (dateOfBirth) => {
  return dateOfBirth ? diff(now(), dateOfBirth, 'years').toString() : ''
}

export const formatDateOfBirth = (dateOfBirth) => {
  return dateOfBirth
    ? format(
        parseFormat(dateOfBirth, DATE_WITH_DASH),
        DATE_DAY_SHORT_STRING_MONTH_YEAR,
      )
    : ''
}

export const formatYearOfBirth = (dateOfBirth) => {
  return dateOfBirth
    ? format(parseFormat(dateOfBirth, DATE_WITH_DASH), YEAR_ONLY)
    : ''
}

// Use formats from datetime/helpers, e.g. STANDARD_DATETIME
export function formatTime(timeStamp, fmt) {
  return !!timeStamp && !!fmt ? parse(timeStamp).format(fmt) : ''
}

// Use formats from datetime/helpers, e.g. STANDARD_DATETIME
export function formatLocalTime(timeStamp, fmt) {
  return !!timeStamp && !!fmt ? parse(timeStamp).local().format(fmt) : ''
}

export function formatStdTimeStamp(timeStamp) {
  return formatTime(timeStamp, AUSTRALIAN_HUMAN_READABLE_DATETIME)
}

export function formatStdDateStamp(timeStamp) {
  return formatTime(timeStamp, DATE_DAY_SHORT_STRING_MONTH_YEAR)
}

export function formatTimeFromGivenTimestamp(timeStamp, givenTimeStamp, fmt) {
  return !!timeStamp && !!fmt
    ? parseFormat(timeStamp, givenTimeStamp).format(fmt)
    : ''
}

// Could be moved to a standard component if styling is required
export const formattedDateOfBirth = (dateOfBirth, isChild = false) => {
  const formattedDate = isChild
    ? formatYearOfBirth(dateOfBirth)
    : formatDateOfBirth(dateOfBirth)
  const parsedDateOfBirth = dateOfBirth ? formattedDate : ''
  const age = dateOfBirth ? calculateAge(dateOfBirth) : ''
  return dateOfBirth
    ? `Year of Birth: ${parsedDateOfBirth} ${!isNaN(age) ? `(${age} yrs)` : ''}`
    : ''
}

export const convertBETimeFormatToGivenFormat = (date, givenTimeFormat) => {
  if (date) {
    return format(parseFormat(date, DATE_WITH_DASH), givenTimeFormat)
  }
  return ''
}

export const getCurrentDate = (fmt) => {
  return format(now(), fmt)
}

export const getCurrentUTCTime = () => utc()

export const getDateWithoutYear = (date) => {
  return format(date, DATE_NO_YEAR)
}

export const getStartOfLastCalendarYear = () => {
  return format(
    subtract(startOf(now(), 'year'), 1, 'year'),
    DEFAULT_UNIX_TIME_Z,
  )
}

export const getEndOfLastCalendarYear = () => {
  return format(subtract(endOf(now(), 'year'), 1, 'year'), DEFAULT_UNIX_TIME_Z)
}

export const getStartOfPriorWeekend = () => {
  // -1 is day number for previous Saturday
  // https://momentjs.com/docs/#/get-set/day/
  return format(startOf(day(now(), -1), 'day'), DEFAULT_UNIX_TIME_Z)
}

export const getEndOfPriorWeekend = () => {
  // 0 is day number for previous Sunday
  return format(endOf(day(now(), 0), 'day'), DEFAULT_UNIX_TIME_Z)
}

// Copied from sm helpers, TODO: should be moved here
const yearArrayFromStartToCurrent = (start) => {
  let yearStart = start
  let yearEnd = parseInt(year(now()))
  return Array(yearEnd - yearStart + 1)
    .fill()
    .map(() => yearEnd--)
}

// TODO: Refactor, then reuse exported yearsFrom1950WithGuidAndName instead of duplicating the calculation in multiple places
const yearsFrom1950 = yearArrayFromStartToCurrent(1950)
export const yearsFrom1950WithGuidAndName = yearsFrom1950.map((yearValue) => ({
  guid: yearValue,
  name: yearValue,
}))

export const getDiffInDays = (startDate, endDate = now()) => {
  return diff(endDate, startDate, 'd')
}

export const getMostRecentTwoYearsFromNow = () => {
  return format(subtract(utc(), 2, 'y'), 'YYYY-MM-DD')
}

export const aggregateTotalRentingDurationInTheLast2Years = (rentalHistory) => {
  const mostRecent2YearsFrom = getMostRecentTwoYearsFromNow()
  if (rentalHistory) {
    const days = rentalHistory.reduce((total, history) => {
      const fromMoment = parse(history.from)
      const toMoment = parse(history.to)
      if (
        !history.isRollingLease &&
        libIsBefore(toMoment, mostRecent2YearsFrom)
      )
        return total

      if (history.isRollingLease) return diff(now(), fromMoment, 'd') + total

      const startPeriodMoment = libIsBefore(fromMoment, mostRecent2YearsFrom)
        ? parse(mostRecent2YearsFrom)
        : fromMoment
      const endPeriodMoment = isAfter(toMoment, now()) ? now() : toMoment
      const periodInDays = diff(endPeriodMoment, startPeriodMoment, 'd')
      return total + periodInDays
    }, 0)

    return helpers.getDurationInYearsMonths(days)
  }
  return 0
}

export const humanizedDateDuration = (endDate, startDate) => {
  const msDiff = diff(startOf(startDate, 'day'), startOf(endDate, 'day'))
  return humanizeDuration(msDiff)
}

export const formattedUTCDisplayDate = (date) => {
  return !!date ? format(utc(date), DisplayDateFormat) : date
}

export const formattedUTCDisplayDateWithoutDay = (date) => {
  return !!date ? format(utc(date), DATE_DAY_SHORT_STRING_MONTH_YEAR) : date
}

export const formattedUTCDisplayDateWithTime = (date) => {
  return !!date ? format(utc(date), DisplayDateWithTimeFormat) : date
}

export const formattedDisplayDateWithoutDay = (date) => {
  return !!date ? format(date, DATE_DAY_SHORT_STRING_MONTH_YEAR) : date
}

export const toTimeAmPmDateFormat = (date) => {
  return format(date, TIME_AM_PM_DATE)
}

export const beforeToday = (date) => libIsBefore(date, new Date(), 'day')

export const isToday = (date) =>
  isSame(date, parseFormat(new Date(), DATE_WITH_DASH), 'day')

export const addMonthsToDate = (date, months) => {
  return !!date || !!months ? calendar(add(date, months, 'months')) : ''
}

export const getPropertyAvailabilityDate = (
  date,
  fmt = helpers.dateWithNoYearFormat,
) => (isSameOrBefore(date, now()) ? 'Now' : format(date, fmt))

export const checkIfPetYOBIsValid = (petDate) => {
  if (petDate) {
    const invalidDate = parseFormat(DEFAULT_UNIX_TIME, 'YYYY-MM-DD')
    const isBirthDateValid = isAfter(
      parseFormat(petDate, DATE_WITH_DASH),
      invalidDate,
    )
    return isBirthDateValid
  } else return false
}

export const getDaysHoursNumFromHours = (numHours) => {
  var days = Math.floor(numHours / 24)
  var hours = Math.floor(numHours) % 24
  return { days, hours }
}

export const getStartOfCurrentWeek = () =>
  format(utc(startOf(now(), 'week')), DATE_TIME_Z)

export const getStartOfPriorWeek = () =>
  format(utc(subtract(startOf(now(), 'week'), 1, 'week')), DATE_TIME_Z)

export const getEndOfPriorWeek = () =>
  format(utc(subtract(endOf(now(), 'week'), 1, 'week')), DATE_TIME_Z)

export const getStartOfCurrentMonth = () =>
  format(utc(startOf(now(), 'month')), DATE_TIME_Z)

export const getStartOfNextMonth = () =>
  add(startOf(now(), 'month'), 1, 'month')

export const getStartOfPriorMonth = () =>
  format(utc(subtract(startOf(now(), 'month'), 1, 'month')), DATE_TIME_Z)

export const getEndOfPriorMonth = () =>
  format(utc(endOf(subtract(now(), 1, 'month'), 'month')), DATE_TIME_Z)

export const getStartOfPriorYearCurrentMonth = () =>
  format(
    utc(subtract(startOf(subtract(now(), 1, 'year'), 'month'), 1, 'month')),
    DATE_TIME_Z,
  )

export const getEndOfPriorYearCurrentMonth = () =>
  format(
    utc(endOf(subtract(subtract(now(), 1, 'year'), 1, 'month'), 'month')),
    DATE_TIME_Z,
  )

export const getStartOfCurrentYear = () =>
  format(startOf(now(), 'year'), DEFAULT_UNIX_TIME_Z)

export const getHalfDayHalfMonthFormattedDate = (date) =>
  format(date, HALF_DAY_HALF_MONTH_TIME_AM_PM)

export const getDateValue = (date) => toDate(date)

export const formatPayloadDate = (date) =>
  !!date ? format(date, DATE_WITH_DASH) : date

export const isTooLongTimeDiff = (date) => diff(now(), date, 'years') > 1000

// Both operands parsed using the same input format before comparison.
// Reversed parameter semantics are preserved: returns true when `compDate`
// is before `refDate`.
export const isBefore = (refDate, compDate, formatDate) =>
  libIsBefore(
    parseFormat(compDate, formatDate),
    parseFormat(refDate, formatDate),
  )

export const ageInDays = (date) => diff(now(), date, 'd')

export const nanoSecToMinute = (nano) => {
  return Math.floor(nano / 1000 / 1000 / 1000 / 60)
}

export const isDateEmpty = (date) =>
  date === INVALID_DATE_STRING || date.includes('0001')

export const getDateOfBirthText = (dateOfBirth) =>
  isDateEmpty(dateOfBirth) ? 'Not Available' : dateOfBirth

export const formatFilterRangeToUTC = (range) => ({
  ...(range?.startDate && {
    startDate: format(
      utc(startOf(range.startDate, 'day')),
      DEFAULT_UNIX_TIME_Z,
    ),
  }),
  ...(range?.endDate && {
    endDate: format(utc(endOf(range.endDate, 'day')), DEFAULT_UNIX_TIME_Z),
  }),
})

export const formatFilterRangeToReadable = (range) => ({
  ...(range?.startDate && {
    startDate: format(utc(startOf(range.startDate, 'day')), STANDARD_DATE),
  }),
  ...(range?.endDate && {
    endDate: format(utc(endOf(range.endDate, 'day')), STANDARD_DATE),
  }),
})

export const todaysDate = () => format(now(), DATE_DAY_SHORT_STRING_MONTH_YEAR)

export const isOlderThanMonths = (date, monthsNum) => {
  const monthsAgo = subtract(now(), monthsNum, 'months')
  return libIsBefore(date, monthsAgo)
}

const STATE_TO_TZ = {
  NSW: 'Australia/Sydney',
  WA: 'Australia/Perth',
  VIC: 'Australia/Melbourne',
  TAS: 'Australia/Hobart',
  QLD: 'Australia/Brisbane',
  NT: 'Australia/Darwin',
  SA: 'Australia/Adelaide',
  ACT: 'Australia/Canberra',
}

/**
 * Returns the effective lease start date for utility referral checks.
 * Lease offer date is the source of truth for UCS; falls back to application date when no lease offer.
 * @param {Object} application - Application object (may have leaseStartDate)
 * @param {Object} [leaseOffer] - Lease offer object (may have leaseStartDate)
 * @returns {string|undefined} The date to use for utility referral past-date checks
 */
export const getEffectiveLeaseStartForUtilityReferral = (
  application,
  leaseOffer,
) => leaseOffer?.leaseStartDate ?? application?.leaseStartDate

/**
 * Returns true if leaseStartDate is before today in the property's timezone.
 * Uses Sydney as fallback when state is empty or unknown.
 */
export const isLeaseStartDateInPast = (leaseStartDate, propertyState) => {
  if (!leaseStartDate) return false
  const timezone =
    STATE_TO_TZ[propertyState?.toUpperCase()] || 'Australia/Sydney'
  const todayStr = format(tz(now(), timezone), DATE_WITH_DASH)
  const leaseStr = format(leaseStartDate, DATE_WITH_DASH)
  return leaseStr < todayStr
}
